import React from "react";
import { formatMoney } from "accounting-js";
import { SAAS_DISCOUNT, OPPORTUNITY_TYPES_NO_DISCOUNT } from "../../constants";
import style from "./SaasProductSelection.module.css";
import { Badge } from "react-bootstrap";
import QuantityInput from "../QuantityInput";
import { clamp } from "lodash-es";
import {connect} from "react-redux";
import { skusUnderWarranty } from "../../redux/selectors";
import { FormControl, InputGroup } from "react-bootstrap";
import { OnBlurInput } from "../OnBlurInput";

const SaasProductSelection = ({
  handleQtyChange,
  handleTargetPriceChange,
  handleSelectionClick,
  isActive,
  selection,
  type,
  linkedParts,
  skusUnderWarranty,
  bundle,
  opportunityType
}) => {
  const product = selection.product;
  const isUserDefined =
    selection.selection_can_change_qty === "1";
  const hasPrice = product.price !== null;
  // The selection price would include the price of linked parts as well.
  // See https://canonau.atlassian.net/browse/M2-573

  const currentSelection = selection;
  const price = hasPrice
    ? selection.is_saas
      ? linkedParts.reduce(
        (price, { selection }) =>
          // One linked part can be linked with multiple products, its qty is the total qty of all linking products, so it is not reliable.
          // Qty of linked part selection should always be the same as the qty of the given product
          // As a consequence, the price should multiply by the qty of current product,
          price + Number(selection.targetPrice) * currentSelection.selection_qty * bundle.subscriptionTerm,
          Number(currentSelection.targetPrice) * currentSelection.selection_qty * bundle.subscriptionTerm
        )
        : linkedParts.reduce(
          (price, { selection }) => 
            // One linked part can be linked with multiple products, its qty is the total qty of all linking products, so it is not reliable.
            // Qty of linked part selection should always be the same as the qty of the given product
            // As a consequence, the price should multiply by the qty of current product,
            price + Number(selection.product.price) * currentSelection.selection_qty,
            Number(currentSelection.product.price) * currentSelection.selection_qty
          )
    : null;
  const isEnabled = product.status === "1";
  const isDefault = selection.is_default === "1";
  const canAddToCart = isEnabled && hasPrice;

  let errorMessage = "";
  if (!isEnabled) {
    if (selection.includes_main_unit === "1") {
      errorMessage = "The main unit is disabled.";
    } else if (isDefault) {
      errorMessage = "The default product is disabled.";
    } else {
      errorMessage = "The product is disabled.";
    }
  } else if (!hasPrice) {
    if (selection.includes_main_unit === "1") {
      errorMessage =
        "The main unit is not associated with the current price list.";
    } else if (isDefault) {
      errorMessage =
        "The default product is not associated with the current price list.";
    } else {
      errorMessage =
        "The product is not associated with the current price list.";
    }
  }

  const linkedProducts = linkedParts.map(part => part.selection.product);
  const disabledLinkedProducts = linkedProducts.filter(
    product => product.status === "2"
  );
  const linkedProductsWithoutPrice = linkedProducts.filter(
    product => product.price === null
  );

  if (disabledLinkedProducts.length) {
    errorMessage = `${errorMessage} The linked product(s) ${disabledLinkedProducts
      .map(product => product.sku)
      .join(", ")} is/are disabled.`;
  }
  if (linkedProductsWithoutPrice.length) {
    errorMessage = `${errorMessage} The linked product(s) ${linkedProductsWithoutPrice
      .map(product => product.sku)
      .join(", ")} is/are not associated with the current price group.`;
  }

  const isUnderWarranty = skusUnderWarranty.includes(product.sku);

  const isTargetSaasTotalValid = () => {
    return OPPORTUNITY_TYPES_NO_DISCOUNT.includes(opportunityType) 
    ? Number(selection.targetPrice) === Number(selection.product.price)
    : Number(selection.targetPrice) >= Number(selection.product.price * SAAS_DISCOUNT);
  }

  return hasPrice ? (
    <tr
      className={`${style.root} ${isActive ? style.active : ""} ${
        canAddToCart ? "" : style.disabled
      }`}
      onClick={() =>
        handleSelectionClick(selection, isActive ? "remove" : "add")
      }
    >
      {/* the radio button/checkbox */}
      <td className={style.itemSelectContainer}>
        <input
          type={["checkbox", "multi"].includes(type) ? "checkbox" : "radio"}
          checked={isActive}
          className={style.itemControl}
          readOnly
        />
      </td>

      {/* product title */}
      <td className={style.itemDescription}>
        <p className={style.title}>
          {product.name}{" "}
          {selection.is_software && <Badge variant="danger">Software</Badge>}
          {selection.is_saas && <Badge className={style.badgeSaas}>SaaS</Badge>}
          {isUnderWarranty && <Badge variant="success">Warranty</Badge>}
        </p>

        <p className={style.details}>
          <span className="text-muted small">{product.short_description}</span>

          {linkedProducts.length > 0 && (
            <span className="text-warning small">
              The price includes additional linked parts:
              {linkedProducts.map(product => product.sku).join(", ")}
            </span>
          )}

          {errorMessage && (
            <span className="text-danger small">
              {errorMessage} Therefore the product(s) will not be added into the
              quote. Please contact the Product Marketing Team.
            </span>
          )}
        </p>
      </td>

      {/* quantity */}
      <td className={style.selectionQtyCell}>
        {isUserDefined ? (
          <React.Fragment>
            ×
            <QuantityInput
              type="number"
              className={style.selectionQty}
              value={selection.selection_qty}
              min={selection.product.simple_saas_min_qty || 1}
              max={selection.product.simple_saas_max_qty || "unlimited"}
              onConfirm={e => handleQtyChange(selection.selection_id, clamp(e.target.value, selection.product.simple_saas_min_qty || 1, selection.product.simple_saas_max_qty || 99999999999)) }
              onClick={e => e.stopPropagation()}
            />
          </React.Fragment>
        ) : null}
      </td>

      {/* minimum sell price */}
      <td className={style.itemDisplayPriceContainer}>
        { formatMoney(Number(selection.product.price)) }
      </td>

      {/* target price */}
      <td className={style.itemDisplayPriceContainer}>
      {selection.is_saas && (
        <InputGroup>
          <InputGroup.Append>
            <InputGroup.Text>$</InputGroup.Text>
          </InputGroup.Append>
          <FormControl
            as={OnBlurInput}
            type="number"
            value={selection.targetPrice}
            onConfirm={e => handleTargetPriceChange(selection.selection_id, e.target.value) }
            min={
              OPPORTUNITY_TYPES_NO_DISCOUNT.includes(opportunityType)
              ? selection.product.price 
              : selection.product.price * SAAS_DISCOUNT}
            max={
              OPPORTUNITY_TYPES_NO_DISCOUNT.includes(opportunityType)
              ? selection.product.price
              : null
            }
            isInvalid={!isTargetSaasTotalValid()}
            onClick={e => e.stopPropagation()}
            disabled={ OPPORTUNITY_TYPES_NO_DISCOUNT.includes(opportunityType) }
          />
        </InputGroup>
        )
      }
      </td>

      {/* term */}
      {selection.is_saas
      ? <td className={style.itemDisplayPriceContainer}>{ bundle.subscriptionTerm } Months</td>
      : <td className={style.itemDisplayPriceContainer}></td>
      }

      {/* finance type */}
      <td className={style.itemDisplayPriceContainer}>
        {selection.financeType}
      </td>

      {/* price */}
      <td className={style.itemDisplayPriceContainer}>
        {price !== null ? formatMoney(price) : "N/A"}
      </td>
    </tr>
  ) : null;
}


export default connect( state => ({
  skusUnderWarranty: skusUnderWarranty(state),
  bundle: state.product,
  opportunityType: state.opportunity.OpportunityType
}))
(SaasProductSelection);
